// ============================================================
// QUOTES ADMIN HOOK - CLIENT-SIDE FILTERING CACHE
// ============================================================
// Strategy: Load all quotes once, cache in memory, filter client-side

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Quote } from '../types/quotes.types';

export interface QuotesAdminState {
  quotes: Quote[]; // Toutes les quotes chargées (cache)
  filteredQuotes: Quote[]; // Quotes après filtre (pour affichage)
  allQuotes: Quote[];
  loading: boolean;
  error: string | null;
  pagination: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
  stats: {
    total: number;
    sent: number;
    accepted: number;
    expiredRejected: number;
  };
  filteredStats?: {
    total: number;
    sent: number;
    accepted: number;
    expiredRejected: number;
  };
}

export interface UseQuotesAdminReturn extends QuotesAdminState {
  statusFilter: string;
  typeFilter: string;
  searchQuery: string;
  updateFilter: (key: string, value: string) => void;
  updateSearch: (query: string) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
}

const DEFAULT_PAGE_SIZE = 10;

export function useQuotesAdmin(): UseQuotesAdminReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const [allQuotes, setAllQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const statusFilter = searchParams.get('status') ?? 'all';
  const typeFilter = searchParams.get('type') ?? 'all';
  const searchQuery = searchParams.get('search') ?? '';
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize = Number(searchParams.get('pageSize') ?? DEFAULT_PAGE_SIZE);

  // Chargement initial de toutes les quotes
  useEffect(() => {
    const fetchAllQuotes = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get('/quotes/admin/all?page=1&pageSize=3000');
        if (!data.success) {
          throw new Error(data.error || 'Erreur lors de la récupération des devis');
        }
        const { items } = data.data;
        setAllQuotes(items);
      } catch (err: any) {
        console.error('[useQuotesAdmin] Fetch error:', err);
        setError(err.message || 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };
    fetchAllQuotes();
  }, []);

  // Filtrage client-side
  const filteredQuotes = useMemo(() => {
    let result = allQuotes;

    // Filtre par statut
    if (statusFilter !== 'all') {
      result = result.filter((q) => q.status === statusFilter);
    }

    // Filtre par type (Standard/Custom) basé sur amount
    if (typeFilter !== 'all') {
      result = result.filter((q) => {
        if (typeFilter === 'Standard') return q.amount < 1500;
        if (typeFilter === 'Custom') return q.amount >= 1500;
        return true;
      });
    }

    // Recherche textuelle
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (q) =>
          q.ref.toLowerCase().includes(query) ||
          q.client.toLowerCase().includes(query) ||
          q.space.toLowerCase().includes(query),
      );
    }

    return result;
  }, [allQuotes, statusFilter, typeFilter, searchQuery]);

  // Pagination côté client
  const totalPages = Math.ceil(filteredQuotes.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const paginatedQuotes = filteredQuotes.slice(startIndex, startIndex + pageSize);

  // Stats GLOBALES (toutes les quotes)
  const globalStats = useMemo(
    () => ({
      total: allQuotes.length,
      sent: allQuotes.filter((q) => q.status === 'SENT').length,
      accepted: allQuotes.filter((q) => q.status === 'ACCEPTED').length,
      expiredRejected: allQuotes.filter((q) => ['EXPIRED', 'REJECTED'].includes(q.status)).length,
    }),
    [allQuotes],
  );

  // Stats FILTRÉES (pour usage interne si besoin)
  const filteredStats = useMemo(
    () => ({
      total: filteredQuotes.length,
      sent: filteredQuotes.filter((q) => q.status === 'SENT').length,
      accepted: filteredQuotes.filter((q) => q.status === 'ACCEPTED').length,
      expiredRejected: filteredQuotes.filter((q) => ['EXPIRED', 'REJECTED'].includes(q.status)).length,
    }),
    [filteredQuotes],
  );

  const updateFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        if (value === 'all') {
          prev.delete(key);
        } else {
          prev.set(key, value);
        }
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const updateSearch = useCallback(
    (query: string) => {
      setSearchParams((prev) => {
        if (query.trim()) {
          prev.set('search', query);
        } else {
          prev.delete('search');
        }
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const setPage = useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        prev.set('page', String(page));
        return prev;
      });
    },
    [setSearchParams],
  );

  const setPageSize = useCallback(
    (size: number) => {
      setSearchParams((prev) => {
        prev.set('pageSize', String(size));
        prev.set('page', '1');
        return prev;
      });
    },
    [setSearchParams],
  );

  return {
    quotes: paginatedQuotes,
    filteredQuotes,
    allQuotes,
    loading,
    error,
    pagination: {
      total: filteredQuotes.length,
      page,
      pageSize,
      totalPages,
    },
    stats: globalStats, // Stats GLOBALES pour les KPI cards
    filteredStats, // Stats filtrées (optionnel)
    statusFilter,
    typeFilter,
    searchQuery,
    updateFilter,
    updateSearch,
    resetFilters,
    setPage,
    setPageSize,
  };
}
